import type { TouchEvent as ReactTouchEvent } from "react";
import { useCallback, useRef } from "react";

export type LongPressOptions = {
  delay?: number;
  onLongPress?: () => void;
};

export const useLongPress = (options: LongPressOptions = {}) => {
  const { delay = 500, onLongPress } = options;

  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const isLongPressing = useRef(false);

  const onTouchStart = useCallback(() => {
    isLongPressing.current = false;
    timeoutRef.current = setTimeout(() => {
      isLongPressing.current = true;
      onLongPress?.();
    }, delay);
  }, [delay, onLongPress]);

  const onTouchCancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
    isLongPressing.current = false;
  }, []);

  const onTouchEnd = useCallback(
    (event?: ReactTouchEvent | TouchEvent) => {
      const wasLongPress = isLongPressing.current;
      onTouchCancel();

      if (wasLongPress && event) {
        event.preventDefault();
      }

      return wasLongPress;
    },
    [onTouchCancel],
  );

  return {
    onTouchStart,
    onTouchEnd,
    onTouchCancel,
  };
};
